import { Entity, Vec3, Layer, StandardMaterial, Quat } from "playcanvas";
import { createXZSpriteEntity } from "@animech/axiom-scene-utilities";
import { assert } from "@animech/core-utilities";
import {
	ControllerBase,
	ControllerEvent,
	ControllerInterface,
	ControllerObject,
	CursorStyle,
	DragPickingPolicy,
	SceneObject,
} from "@animech/axiom-scene-builder";
import { vec3ToVector, vectorToVec3 } from "@animech/axiom-utility";
import { isProperty, Vector } from "@animech/axiom-format";
import { Root } from "@definitions";

export interface RotateControllerApi {
	snapAxisValue?(axis: "x" | "y" | "z", currentValue: number): number;
}

interface DragState {
	entity: Entity;
	axis: "x" | "y" | "z";
	startVector: Vec3;
	startRotation: Quat;
	tempRotation: Quat;
	startPointerPosition: Vec3;
}

export class RotateController extends ControllerBase<Root> {
	private _iconMaterial: StandardMaterial;
	private _spriteEntity?: Entity;
	private _dragState?: DragState;
	private _releaseRenderRequest?: () => void;
	private _api: RotateControllerApi;

	constructor(iconMaterial: StandardMaterial, api: RotateControllerApi) {
		super();
		this._iconMaterial = iconMaterial;
		this._api = api;
	}

	get hoverCursorStyle(): CursorStyle {
		return "move";
	}

	get dragCursorStyle(): CursorStyle {
		return "grabbing";
	}

	/** Rotation follows pointer coordinates only, so only the start needs a GPU hit. */
	get dragPicking(): DragPickingPolicy {
		return "start-only";
	}

	/** Restores the start rotation of an in-progress drag, without a configuration action. */
	onDragInvalidated(): void {
		const dragState = this._dragState;
		this._dragState = undefined;
		const release = this._releaseRenderRequest;
		this._releaseRenderRequest = undefined;
		try {
			dragState?.entity.setRotation(dragState.startRotation);
		} finally {
			release?.();
		}
	}

	canHandle(event: ControllerEvent): boolean {
		const isDragging = !!this._dragState;

		if (event.type === "dragStart") {
			return !isDragging;
		} else if (event.type === "dragMove" || event.type === "dragEnd") {
			return isDragging;
		} else {
			return false;
		}
	}

	onEvent(
		event: ControllerEvent,
		object: ControllerObject<Root>,
		controllerInterface: ControllerInterface<Root>,
	): boolean {
		const { x, y } = event;
		const {
			getConfigurationValue,
			pushAutoRenderRequest,
			popAutoRenderRequest,
			getEntity,
			onAction,
		} = controllerInterface;
		const pointerPosition = new Vec3(x, y, 0);
		const targetObject = object.targetObject;
		assert(
			targetObject,
			`Controller object with name "${object.name}" has no target`,
		);
		const entity = getEntity(targetObject);
		if (!pointerPosition || !entity) {
			return true;
		}
		if (event.type === "dragStart") {
			if (this._dragState || !this._spriteEntity || !object.vector) {
				return true;
			}
			const localRotationVector = entity
				.getWorldTransform()
				.clone()
				.invert()
				.transformVector(this._spriteEntity.up);
			const axis = (["x", "y", "z"] as const).find(
				(axis) => Math.abs(localRotationVector[axis]) > 0.99,
			);
			if (!axis) {
				return true;
			}
			this._dragState = {
				entity,
				axis,
				startVector: vectorToVec3(
					getConfigurationValue<Vector>(object.vector, object.archetype),
				),
				startRotation: entity.getRotation().clone(),
				tempRotation: new Quat(),
				startPointerPosition: pointerPosition,
			};
			pushAutoRenderRequest();
			this._releaseRenderRequest = popAutoRenderRequest;
		} else if (event.type === "dragMove" || event.type === "dragEnd") {
			if (!this._dragState || !this._spriteEntity) {
				return true;
			}
			const { axis, startVector, startRotation, startPointerPosition } =
				this._dragState;
			const pointerDelta = pointerPosition.sub(startPointerPosition);

			const axisValue = startVector[axis] + pointerDelta.x;
			const snappedAxisValue =
				this._api.snapAxisValue?.(axis, axisValue) ?? axisValue;

			const euler = new Vec3();
			euler[axis] = snappedAxisValue - startVector[axis];

			entity.setRotation(startRotation);
			entity.rotateLocal(euler);

			const newVector = vec3ToVector(entity.getLocalEulerAngles());
			if (event.type === "dragEnd") {
				this._dragState = undefined;
				const vector = object.vector;
				if (vector && isProperty(vector)) {
					onAction({
						action: {
							type: "setProperty",
							property: vector,
							value: newVector,
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
						} as any,
						archetype: object.archetype,
					});
				}
				const release = this._releaseRenderRequest;
				this._releaseRenderRequest = undefined;
				release?.();
				return true;
			}
		}
		return false;
	}

	updateEntity(_object: SceneObject<Root>, entity: Entity, layer: Layer): void {
		if (this.modelEntities.length === 0) {
			const sprite = createXZSpriteEntity(this._iconMaterial, 1 / 1000, layer);
			// Manipulation gizmos are transient UI and must never reach the AR/GLB
			// export (their icon sprites use unnamed runtime textures that crash the
			// exporter). Tag the model's mesh-instance nodes too — the sprite plane
			// lives on the model component's internal graph node, which the exporter
			// visits independently, and the GLB export filters on mesh-instance node
			// tags rather than entity tags.
			sprite.tags.add("excludeFromAR");
			for (const mi of sprite.model?.meshInstances ?? []) {
				mi.node.tags.add("excludeFromAR");
			}
			entity.addChild(sprite);
			this.modelEntities.push(sprite);
			this._spriteEntity = sprite;
		}
	}
}
